import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { Autocomplete, CircularProgress, TextField, Typography } from '@mui/material';
import { useAutocompleteSuggestions } from './useAutocompleteSuggestions';
import { getPlace } from './helper';

export const GooglePlacesAutocomplete = ({
	address,
	onSelectAddress,
	disabled,
	error,
	helperText,
}) => {
	const [inputValue, setInputValue] = useState(address ?? '');

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username ?? null;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const countries = useMemo(
		() => [userDetails?.broker?.country ?? 'NZ'],
		[userDetails?.broker?.country],
	);

	const requestOptions = useMemo(
		() => ({
			includedRegionCodes: countries,
		}),
		[countries],
	);

	const { suggestions, resetSession, isLoading } = useAutocompleteSuggestions(
		inputValue,
		requestOptions,
	);

	const handleInputChange = useCallback((event, value, reason) => {
		if (typeof value === 'string') {
			setInputValue(value);
		}
	}, []);

	const handleSelect = useCallback(
		(event, prediction, reason) => {
			if (typeof prediction === 'string') return;
			if (prediction == null) return setInputValue('');

			const place = prediction.toPlace();
			const predictionText = prediction.text?.text; // Capture the prediction text with "33a"

			place
				.fetchFields({
					fields: ['addressComponents', 'formattedAddress', 'location'],
				})
				.then(() => {
					resetSession();
					onSelectAddress(getPlace(place, predictionText)); // Pass predictionText
					setInputValue('');
				});
		},
		[onSelectAddress],
	);

	const predictions = useMemo(
		() =>
			suggestions
				.filter((suggestion) => suggestion.placePrediction)
				.map((suggestion) => {
					return suggestion.placePrediction;
				}),
		[suggestions],
	);

	return (
		<Autocomplete
			options={predictions}
			disabled={disabled}
			open={Boolean(inputValue)}
			loading={isLoading}
			inputValue={inputValue}
			onInputChange={handleInputChange}
			onChange={handleSelect}
			filterOptions={(x) => x}
			getOptionLabel={(option) => {
				if (typeof option === 'string') return option;
				return option?.text?.text ?? '';
			}}
			isOptionEqualToValue={(a, b) => a.placeId === b.placeId}
			noOptionsText={inputValue ? 'No results' : ''}
			renderInput={(params) => (
				<TextField
					{...params}
					{...{ error, helperText }}
					placeholder={address ?? 'Search for a place'}
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{isLoading ? <CircularProgress size={18} /> : null}
								{params.InputProps.endAdornment}
							</>
						),
					}}
				/>
			)}
			renderOption={(props, option) => {
				return (
					<li {...props} key={option.placeId} style={{ width: '100%' }}>
						<Typography>{option?.text?.text ?? ''}</Typography>
					</li>
				);
			}}
			renderValue={() => <></>}
		/>
	);
};
